import { formatAge } from "@/lib/queue-health";
import type { DeviceStatus } from "./review-client";

/** A device beats every 20s while its app is open. Two minutes of silence
 * is therefore not a slow network — it's an app that was closed, a phone
 * that locked, or a tab the browser evicted. Which is exactly the state
 * that used to be invisible: the device stops uploading and stops saying
 * anything, and from the desk that looked the same as being finished. */
const STALE_MS = 120000;

export function isStale(device: DeviceStatus, now: number): boolean {
  return now - Date.parse(device.seenAt) > STALE_MS;
}

export function deviceCounts(device: DeviceStatus): string {
  const parts: string[] = [];
  if (device.waiting > 0) parts.push(`${device.waiting} waiting`);
  if (device.blocked > 0) parts.push(`${device.blocked} blocked`);
  if (device.needsRetake > 0) parts.push(`${device.needsRetake} need retake`);
  return parts.length === 0 ? "Nothing waiting" : parts.join(" · ");
}

/** Scanner name is what staff know a device by, and it stops identifying
 * anything the moment someone captures from a second phone. Only the
 * ambiguous names get a device-id tail, so the common case stays readable. */
export function duplicatedScannerNames(devices: DeviceStatus[]): Set<string> {
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const device of devices) {
    if (seen.has(device.scannerName)) duplicated.add(device.scannerName);
    seen.add(device.scannerName);
  }
  return duplicated;
}

/** Leading chunk of the device's UUID — the conventional short form, and
 * the only thing that distinguishes two phones under one name. */
export function deviceIdHint(device: DeviceStatus): string {
  return device.deviceId.slice(0, 8);
}

/** Worst first: whoever needs a person to act, then whoever is holding the
 * most. A desk opening this screen during a backlog should not have to
 * read every card to find the one that matters. */
export function sortDevices(devices: DeviceStatus[], now: number): DeviceStatus[] {
  return [...devices].sort((a, b) => {
    const troubled = Number(deviceTrouble(b, now) !== null) - Number(deviceTrouble(a, now) !== null);
    if (troubled !== 0) return troubled;
    const held = b.waiting + b.blocked - (a.waiting + a.blocked);
    if (held !== 0) return held;
    return a.scannerName.localeCompare(b.scannerName);
  });
}

export function deviceSeen(device: DeviceStatus, now: number): string {
  const age = formatAge(now - Date.parse(device.seenAt));
  return age === "just now" ? "reporting now" : `last reported ${age} ago`;
}

const PAUSE_LABEL: Record<string, string> = {
  auth: "its access token was rejected",
  unreachable: "it can't reach the server",
  offline: "it reports being offline",
};

/** The reason this device isn't finishing, in the order worth acting on.
 * Returns null for a device that is simply working. */
export function deviceTrouble(device: DeviceStatus, now: number): string | null {
  if (isStale(device, now) && device.waiting > 0) {
    return `App closed or asleep with ${device.waiting} unsent — ask them to open the scanner app.`;
  }
  if (device.pauseReason) {
    return `Uploads paused: ${PAUSE_LABEL[device.pauseReason] ?? device.pauseReason}.`;
  }
  if (device.maxAttempts >= 6 && device.lastFailReason) {
    return `Failing after ${device.maxAttempts} attempts: ${device.lastFailReason}`;
  }
  if (device.needsRetake > 0) {
    return `${device.needsRetake} page(s) lost on the device — they must be retaken.`;
  }
  return null;
}

/** A push is a request, not a command: it lands on the device's next beat,
 * and a device whose app is closed has no next beat. Showing its age is
 * what keeps that honest — a request still sitting there after ten minutes
 * means nobody has opened that app. */
export function flushRequestedLabel(device: DeviceStatus, now: number): string | null {
  if (!device.flushRequestedAt) return null;
  const age = formatAge(now - Date.parse(device.flushRequestedAt));
  return age === "just now" ? "Push requested just now" : `Push requested ${age} ago`;
}

/** How many devices need someone to do something. Drives the badge on the
 * desk's Devices link, so a stuck phone is visible from the script list
 * without anyone thinking to go looking for it. */
export function troubledDeviceCount(devices: DeviceStatus[] | undefined, now: number): number {
  return (devices ?? []).filter(d => deviceTrouble(d, now) !== null).length;
}

/** Age of the device's oldest unsent script, when it has one. */
export function deviceBacklogAge(device: DeviceStatus, now: number): string | null {
  if (!device.oldestWaitingAt) return null;
  return formatAge(now - Date.parse(device.oldestWaitingAt));
}
